import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { supabase, checkSupabaseConnection } from '../../supabaseClient';
import { FaArrowLeft } from 'react-icons/fa';
import { navigateToResumedWorkspace } from '../../lib/resolveMostRecentWorkspace';

const AuthPage = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Check if we're coming from the landing page with a signup request
    if (location.state) {
      if (location.state.isSignup) {
        setIsLogin(false);
        // Set email if provided from landing page
        if (location.state.email) {
          setEmail(location.state.email);
        }
      } else if (location.state.isLogin) {
        setIsLogin(true);
      }
    }

    // Check if we're coming from email confirmation
    const confirmEmail = async () => {
      const searchParams = new URLSearchParams(location.search);
      const token_hash = searchParams.get('token_hash');
      const type = searchParams.get('type');

      if (token_hash && type) {
        setLoading(true);
        const redirectUrl = `${window.location.origin}/workspace`;

        try {
          const { error } = await supabase.auth.verifyOtp({
            token_hash,
            type,
            options: {
              redirectTo: redirectUrl,
            },
          });
          if (error) throw error;

          const { data: { user } } = await supabase.auth.getUser();
          if (user) {
            await navigateToResumedWorkspace(navigate, user.id, { replace: true });
          } else {
            navigate('/workspace', { replace: true });
          }
        } catch (error) {
          console.error('Error confirming email:', error);
          setError('Error confirming email. Please try again or contact support.');
        } finally {
          setLoading(false);
        }
      }
    };

    confirmEmail();

    const testConnection = async () => {
      try {
        const { connected, error } = await checkSupabaseConnection();
        if (!connected) {
          setError(`Cannot connect to database. Please try again later. ${error?.message || ''}`);
        }
      } catch (err) {
        setError('Connection test failed: ' + err.message);
      }
    };
    
    testConnection();
  }, [location.search, location.state, navigate]);

  const validateInputs = () => {
    if (!email) {
      setError('Email is required');
      return false;
    }
    if (!/\S+@\S+\.\S+/.test(email)) {
      setError('Invalid email format');
      return false;
    }
    if (!password) {
      setError('Password is required');
      return false;
    }
    if (password.length < 6) {
      setError('Password must be at least 6 characters long');
      return false;
    }
    if (!isLogin && !displayName.trim()) {
      setError('Display name is required');
      return false;
    }
    return true;
  };

  // Get location from IP address
  const getLocationFromIP = async () => {
    try {
      const response = await fetch('https://ipapi.co/json/');
      if (response.ok) {
        const data = await response.json();
        return {
          country: data.country_name,
          city: data.city,
          region: data.region,
          latitude: data.latitude,
          longitude: data.longitude,
          timezone: data.timezone,
          ip: data.ip
        };
      }
      return null;
    } catch (error) {
      console.error('Error getting location from IP:', error);
      return null;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');

    if (!validateInputs()) return;

    // Check for internet connection first
    if (!navigator.onLine) {
      setError('No internet connection. Please check your network and try again.');
      return;
    }

    setLoading(true);
    try {
      // Get location data from IP address
      const locationData = await getLocationFromIP();
      
      if (isLogin) {
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) throw error;
        handleSuccessfulLogin();
      } else {
        const redirectUrl = `${window.location.origin}/workspace`;
        
        const { data, error } = await supabase.auth.signUp({ 
          email, 
          password,
          options: {
            emailRedirectTo: redirectUrl,
            data: {
              display_name: displayName.trim(),
              location: locationData || null
            }
          }
        });
        
        if (error) {
          throw error;
        }

        // Check if user is new based on identities array
        if (!data?.user?.identities || data.user.identities.length === 0) {
          setError('An account with this email already exists. Please log in instead.');
          return;
        }

        setSuccessMessage(`Sign up successful! Please check ${email} to verify your account. You may now close this page.`);
      }
    } catch (error) {
      console.error('Authentication error:', error);
      
      // Handle network errors specifically
      if (error.message?.includes('fetch') || error.code === 'NETWORK_ERROR' || error.name === 'AuthRetryableFetchError') {
        setError('Network error: Unable to connect to authentication service. Please check your internet connection and try again.');
      } else if (error.message?.toLowerCase().includes('user already registered')) {
        setError('An account with this email already exists. Please log in instead.');
      } else {
        setError(error.message || 'An error occurred during authentication. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSuccessfulLogin = async () => {
    const urlParams = new URLSearchParams(window.location.search);
    const redirectPath = urlParams.get('redirect');

    if (redirectPath) {
      navigate(decodeURIComponent(redirectPath));
      return;
    }

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        await navigateToResumedWorkspace(navigate, user.id);
        return;
      }
    } catch (error) {
      console.error('Error resolving workspace home:', error);
    }

    navigate('/workspace');
  };

  // Add a timeout to automatically reset loading state if stuck
  useEffect(() => {
    let timeoutId;
    if (loading) {
      timeoutId = setTimeout(() => {
        setLoading(false);
        setError('Request timed out. Please try again or check your internet connection.');
      }, 15000); // 15 seconds timeout
    }
    return () => clearTimeout(timeoutId);
  }, [loading]);

  return (
    <div className="min-h-screen bg-neutral-50 flex flex-col justify-center items-center p-4 sm:p-6 font-['Inter', sans-serif]">
      <div className="w-full max-w-md">
        <Link to="/" className="mb-4 inline-flex items-center text-neutral-900 hover:text-cranberry-700 transition-colors duration-300">
          <FaArrowLeft className="mr-2" />
          <span className="text-sm sm:text-base">Back to Home</span>
        </Link>
        <div className="bg-white rounded-xl shadow-lg p-4 sm:p-8 border-2 border-neutral-900">
          <h2 className="text-2xl sm:text-3xl font-bold text-center mb-4 sm:mb-6 text-neutral-900">
            {isLogin ? 'Log In to Basket' : 'Sign Up for Basket'}
          </h2>
          {loading ? (
            <div className="flex flex-col items-center justify-center py-8 space-y-4">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-cranberry-700"></div>
              <p className="text-center text-neutral-900 font-medium">Authenticating...</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              {!isLogin && (
                <div>
                  <label htmlFor="displayName" className="block text-sm font-medium text-neutral-900">
                    Display Name
                  </label>
                  <input
                    type="text"
                    id="displayName"
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    required
                    className="mt-1 block w-full px-4 py-2 rounded-xl border-2 border-neutral-900 focus:border-cranberry-700 focus:ring-1 focus:ring-cranberry-700 outline-none"
                  />
                </div>
              )}
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-neutral-900">
                  Email
                </label>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="mt-1 block w-full px-4 py-2 rounded-xl border-2 border-neutral-900 focus:border-cranberry-700 focus:ring-1 focus:ring-cranberry-700 outline-none"
                />
              </div>
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-neutral-900">
                  Password
                </label>
                <input
                  type="password"
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="mt-1 block w-full px-4 py-2 rounded-xl border-2 border-neutral-900 focus:border-cranberry-700 focus:ring-1 focus:ring-cranberry-700 outline-none"
                />
              </div>
              {error && (
                <div className="text-sm text-red-600 bg-red-50 border-l-4 border-red-600 p-4 rounded-r-xl">
                  {error}
                </div>
              )}
              {successMessage && (
                <div className="text-sm text-emerald-600 bg-emerald-50 p-4 rounded-xl">
                  {successMessage}
                </div>
              )}
              {isLogin && (
                <div className="text-right">
                  <Link to="/auth/forgot-password" className="text-sm text-cranberry-700 hover:text-cranberry-600 transition-colors duration-300">
                    Forgot password?
                  </Link>
                </div>
              )}
              <button
                type="submit"
                className="w-full flex justify-center py-2 px-6 rounded-xl text-white bg-cranberry-700 hover:bg-cranberry-600 transition-colors duration-300 font-medium border-2 border-neutral-900"
                disabled={loading}
              >
                {loading ? 'Processing...' : (isLogin ? 'Log In' : 'Sign Up')}
              </button>
            </form>
          )}
          {!isLogin && (
            <div className="mt-4 text-sm text-neutral-600">
              <p>✓ Create a strong, unique password</p>
              <p>✓ Verify your email to activate all features</p>
              <p>✓ By signing up, you agree to our Terms of Service and Privacy Policy</p>
            </div>
          )}
          <div className="mt-4 sm:mt-6 text-center">
            <button
              onClick={() => {
                setIsLogin(!isLogin);
                setError('');
                setSuccessMessage('');
              }}
              className="text-base sm:text-lg font-medium text-cranberry-700 hover:text-cranberry-600 transition-colors duration-300 underline"
            >
              {isLogin ? 'Need an account? Sign Up' : 'Already have an account? Log In'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthPage;
